import { useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogTrigger,
} from "@/components/ui/dialog";
import BaseButton from "@/components/base/button/BaseButton";
import GiftIcon from "@/assets/icons/gift.svg?react";
import TaskBaseButton from "@/components/base/button/TaskBaseButton";
import { cn } from "@/lib/utils";

// Icons
import ChevronRight from "@/assets/icons/chevron-right.svg?react";
import CheckIcon from "@/assets/icons/check.svg?react";
import CloseIcon from "@/assets/icons/close.svg?react";
import IconDon from "@/assets/icons/don.svg?react";
import IconAlertTriangle from "@/assets/icons/alert-triangle.svg?react";
import { NextDayCountdown } from "./sections/daily-tasks/DailyCheckIn/DailyCheckIn";
import { WeeklyAwards } from "../api";
import { formatNumber } from "@/shared/utils";

// Types
interface CheckInDialogProps {
  currentDay: number;
  onConfirm?: () => Promise<boolean>;
  isPending: boolean;
  isClaimed: boolean;
  nextCheckInAt?: string | null;
  onDayChange?: () => void;
  /** When supplied, the parent controls the dialog (used by the Premium
   * streak-protection bottom sheet's "DON olish" CTA). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  weeklyAwards: WeeklyAwards[];
  /** Show the streak-lost recovery alert badge in the top-left of the dialog. */
  showRecoveryAlert?: boolean;
  /** Click handler for the alert — typically closes this dialog and opens StreakLostModal. */
  onRecoveryAlertClick?: () => void;
  /** Last day the user actually claimed before the reset. The day after
   *  it is the "skipped" one the warning dialog focuses on. Only set when
   *  showRecoveryAlert is true (freemium user that just reset). */
  previousDayNumber?: number;
}

interface DayReward {
  id: number;
  day: number;
  coin: number | string;
}

// const SPECIAL_REWARD: DayReward = { id: 7, day: 7, coin: 400 };

function getCurrentWeekDays(currentDay: number): number[] {
  const start = Math.floor((currentDay - 1) / 7) * 7 + 1;
  return Array.from({ length: 7 }, (_, i) => start + i);
}

const CheckInDialog = ({
  currentDay,
  onConfirm,
  isPending,
  isClaimed,
  nextCheckInAt,
  onDayChange,
  open,
  onOpenChange,
  weeklyAwards,
  showRecoveryAlert = false,
  onRecoveryAlertClick,
  previousDayNumber,
}: CheckInDialogProps) => {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const { t } = useTranslation();
  const isOpen = open ?? uncontrolledIsOpen;

  const handleConfirm = async () => {
    const wasClaimed = await onConfirm?.();
    if (wasClaimed !== false) {
      handleOpenChange(false);
    }
  };

  const handleDayChange = () => {
    forceUpdate();
    onDayChange?.();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && showRecoveryAlert) {
      onRecoveryAlertClick?.();
      return;
    }
    onOpenChange?.(nextOpen);
    if (open === undefined) {
      setUncontrolledIsOpen(nextOpen);
    }
  };

  const isHighlighted = (day: number) => !isClaimed && day === currentDay;
  const isTodayClaimed = (day: number) => isClaimed && currentDay === day;

  const weekDays = useMemo(() => {
    return getCurrentWeekDays(currentDay);
  }, [currentDay]);

  // Render helper functions
  const renderDayRewardItem = (item: DayReward) => {
    const isCurrentDay = currentDay === item.day;
    const isCompleted = item.day < currentDay || (isCurrentDay && isClaimed);

    return (
      <div key={item.id}>
        <div
          className={cn(
            "flex relative items-center justify-center rounded-xl",
            {
              "border-2 border-orange-100": isHighlighted(item.day),
              "border border-green bg-green/08": isTodayClaimed(item.day),
            }
          )}
          style={{
            boxShadow: isHighlighted(item.day)
              ? "0px 4px 14px 0px rgba(251, 199, 108, 0.32)"
              : "none",
          }}
        >
          {isCompleted && (
            <span className="absolute rounded-full -top-1 -right-1 z-10 size-[18px] bg-green flex-center">
              <CheckIcon />
            </span>
          )}
          <div className="border-b w-full py-2.5 px-4 inline-flex justify-center items-center gap-0.5 border-white/08 rounded-xl backdrop-blur bg-white/08">
            <IconDon width="22" height="22" className="text-gray-1 shrink-0" />
            <p className="font-extrabold text-white text-2xl leading-130 uppercase text-center">
              {formatNumber(item.coin)}
            </p>
          </div>
        </div>
        <p className="text-center text-xs font-normal leading-130 text-gray-1 mt-1.5 lowercase">
          {item.day}-{t("day")}
        </p>
      </div>
    );
  };

  const fallbackAward = { award: "0", bonus: "0" };
  const lastWeeklyAward =
    weeklyAwards[weeklyAwards.length - 1] ?? fallbackAward;
  const SPECIAL_REWARD = {
    day: weekDays[6], // 👈 7th day of the week (index 6)
    coin: lastWeeklyAward.award,
    bonus: lastWeeklyAward.bonus,
  };

  const renderSpecialReward = () => {
    const isCurrentDay = currentDay === SPECIAL_REWARD.day;
    const isCompleted =
      SPECIAL_REWARD.day < currentDay || (isCurrentDay && isClaimed);

    return (
      <div
        className={cn(
          "mt-0 relative border-b overflow-hidden border-white/08 flex items-center px-4 pt-3 pb-2.5 gap-3 justify-between rounded-xl bg-white/08 w-full",
          {
            "border-2 border-orange-100": isHighlighted(SPECIAL_REWARD.day),
            "!overflow-visible": isClaimed && isCurrentDay,
            "border border-green bg-green/08": isTodayClaimed(
              SPECIAL_REWARD.day
            ),
          }
        )}
        style={{
          boxShadow: isHighlighted(SPECIAL_REWARD.day)
            ? "0px 4px 14px 0px rgba(251, 199, 108, 0.32)"
            : "none",
        }}
      >
        {isCompleted && (
          <span className="absolute rounded-full -top-1 -right-1 z-50 size-[18px] bg-green flex-center">
            <CheckIcon />
          </span>
        )}
        <div className="flex items-start flex-col">
          <h1 className="text-yellow text-xs leading-130 font-normal lowercase">
            {SPECIAL_REWARD.day}-{t("day")}
          </h1>
          <div className="flex gap-0.5 items-center justify-center">
            <IconDon
              width="22"
              height="22"
              className="text-gray-1 aspect-square"
            />
            <p className="font-extrabold text-2xl leading-130 text-center flex items-center">
              {formatNumber(SPECIAL_REWARD.coin)}
              <span className="mx-[0.5px]">+</span>
              <span className="text-orange-100">
                {formatNumber(SPECIAL_REWARD.bonus)}
              </span>
              <span>
                <GiftIcon
                  className="size-5 text-orange-100"
                  strokeWidth={1.5}
                />
              </span>
            </p>
          </div>
        </div>
        <img
          className="absolute z-10 bottom-0 right-2 h-full"
          src="images/patterns/check-in-don-bag.webp"
          alt=""
        />
        <img
          className="absolute bottom-0 right-1 h-full z-0"
          src="images/patterns/check-in-calendar.webp"
          alt="Icon"
        />
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger className="w-auto shrink-0" asChild>
        {isClaimed ? (
          <TaskBaseButton className="bg-white/08 text-white">
            <NextDayCountdown
              targetTime={nextCheckInAt}
              onDayChange={handleDayChange}
            />
          </TaskBaseButton>
        ) : (
          <TaskBaseButton className="bg-yellow-linear min-w-[6rem] text-black !w-auto">
            {t("task_status.INCOMPLETE")}
            <ChevronRight className="text-black size-5" />
          </TaskBaseButton>
        )}
      </DialogTrigger>
      <DialogContent
        onPointerDownOutside={(e) => {
          if (!isClaimed || isPending) {
            e.preventDefault();
          }
        }}
        className="bg-gray-linear border-none !rounded-20 border-dialog max-w-[90%]"
      >
        {/* Header decorations */}
        <img
          className="absolute left-1/2 -translate-x-1/2 top-0 -translate-y-1/2 w-[200px] aspect-square pointer-events-none z-10"
          src="images/patterns/check-in-modal.webp"
          alt="Don logo"
        />
        <DialogClose className="absolute top-4 right-4 z-10 size-[28px] bg-white/08 rounded-full flex-center outline-none ring-0 shadow-none border-none">
          <CloseIcon className="size-4" />
        </DialogClose>

        {/* Streak-lost recovery badge — pulses red while the 24h
            window is open. Style mirrors DialogClose (white/08 border)
            at the symmetric corner. Click closes this dialog and the
            parent opens StreakLostModal. */}
        {showRecoveryAlert && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenChange(false);
              setTimeout(() => onRecoveryAlertClick?.(), 180);
            }}
            aria-label={t("streak_lost_open") || "Streak ma'lumoti"}
            className="absolute top-4 left-4 z-20 size-[28px] flex-center"
          >
            <span className="absolute inline-flex size-full rounded-full bg-red/70 animate-ping pointer-events-none" />
            <span className="relative size-[28px] rounded-full bg-red flex-center border border-white/08">
              {/* Triangle centroid sits ~2px below geometric center
                  (bottom-heavy shape) — shift up so it looks centered. */}
              <IconAlertTriangle className="size-[18px] text-white -translate-y-[1px]" />
            </span>
          </button>
        )}

        {/* Dialog header */}
        <DialogHeader className="pt-[38px] flex items-center">
          <DialogTitle className="text-center text-2xl text-white leading-130 font-extrabold uppercase self-stretch mb-1">
            {t("weekly_check_in_modal")}
          </DialogTitle>
          <p className="text-center px-2 text-white/80 font-normal leading-130 text-xs mb-6">
            {t("daily_visit_reward")}
          </p>
        </DialogHeader>

        {/* Daily rewards grid.
            When the freemium user just reset (showRecoveryAlert + we know
            previousDayNumber), we collapse the 7-day grid into a single
            highlighted card representing the day they skipped — the rest
            stays hidden so the modal's focus is "this is what you lost". */}
        {showRecoveryAlert && previousDayNumber ? (
          (() => {
            const missedDay = previousDayNumber + 1;
            // Day number → award index in the week's 7-entry list.
            const idx = ((missedDay - 1) % 7);
            const award =
              weeklyAwards[idx] || lastWeeklyAward;
            return (
              <div className="grid grid-cols-3 gap-3">
                <div className="invisible" aria-hidden />
                {renderDayRewardItem({
                  coin: parseInt(award.award),
                  day: missedDay,
                  id: parseInt(award.award),
                })}
                <div className="invisible" aria-hidden />
              </div>
            );
          })()
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              {weeklyAwards.slice(0, 6).map((item, index) =>
                renderDayRewardItem({
                  coin: parseInt(item.award),
                  day: weekDays[index],
                  id: parseInt(item.award),
                })
              )}
            </div>

            {/* Special reward (7-day) */}
            {renderSpecialReward()}
          </>
        )}

        {/* Confirm button */}
        {isClaimed ? (
          <TaskBaseButton
            className="bg-white/08 text-sm rounded-xl text-white py-3 px-4 min-h-[43px] gap-2"
            disabled
          >
            {t("until_tomorrow_timer")}:
            <NextDayCountdown
              targetTime={nextCheckInAt}
              onDayChange={handleDayChange}
              className="[&_span]:text-sm"
            />
          </TaskBaseButton>
        ) : (
          <BaseButton
            text={t("task_status.PERFORMED")}
            className="bg-yellow-linear disabled:bg-none disabled:!bg-white/08 text-dark"
            onClick={handleConfirm}
            isLoading={isPending}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CheckInDialog;
